import LegalPage from "@/components/LegalPage";

export default function ReturnsPolicy() {
  return (
    <LegalPage title="Refunds, Replacements & Order Issues Policy" testId="returns-policy-page">
      <p>True Impact Peptides</p>
      <p>Last Updated: August 2026</p>

      <p>At True Impact Peptides, we are committed to providing high-quality Research Use Only (RUO) products and exceptional customer service.</p>
      <p>Due to the specialized nature of our products and the importance of maintaining product integrity, quality assurance, and chain of custody, we do not accept product returns.</p>
      <p>This policy explains how order issues, fulfillment errors, damaged shipments, and other concerns are handled.</p>
      <p>By placing an order through our Website, you acknowledge and agree to this Policy.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">1. Research Use Only Products</h3>
      <p>All products sold by True Impact Peptides are intended exclusively for lawful laboratory research purposes. Because these products are designated Research Use Only (RUO), they require strict quality control throughout manufacturing, storage, and distribution. Once a product has left our possession, we cannot verify how it has been stored, transported, handled, or maintained. For this reason, returned products cannot be accepted, restocked, or resold.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">2. No Returns Accepted</h3>
      <p>True Impact Peptides does not accept returns of products once they have been shipped. All sales are considered final except where otherwise required by applicable law. Customers should carefully review their order before completing checkout. If you have questions about a product before purchasing, please contact us at info@trueimpactpeptides.com. We are happy to answer product questions before an order is placed.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">3. Order Issues</h3>
      <p>Although returns are not accepted, we are committed to resolving legitimate fulfillment issues. If there is a problem with your order, please contact us promptly so we can investigate. Examples include:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Incorrect product received</li>
        <li>Missing product</li>
        <li>Damaged shipment</li>
        <li>Incorrect quantity</li>
        <li>Fulfillment error</li>
      </ul>
      <p>Each situation is reviewed individually.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">4. Replacement Eligibility</h3>
      <p>True Impact Peptides may provide a replacement product if we determine that the issue resulted from an error on our part. Examples include:</p>
      <p className="font-medium text-slate-800">Incorrect Product Shipped</p>
      <p>If we accidentally shipped the wrong product.</p>
      <p className="font-medium text-slate-800">Missing Item</p>
      <p>If an item was omitted from your shipment.</p>
      <p className="font-medium text-slate-800">Damaged During Transit</p>
      <p>If your shipment arrives visibly damaged during transportation. Photographs of both the shipping package and the product may be required.</p>
      <p className="font-medium text-slate-800">Fulfillment Error</p>
      <p>If an error occurred during packaging or shipment. Replacement decisions are made at the sole discretion of True Impact Peptides after review of the circumstances.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">5. Situations Not Eligible for Refund or Replacement</h3>
      <p>Refunds or replacements generally will not be provided for:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Buyer's remorse.</li>
        <li>Incorrect product selection by the customer.</li>
        <li>Orders placed in error.</li>
        <li>Products no longer needed.</li>
        <li>Shipping delays caused by the carrier.</li>
        <li>Incorrect shipping information provided during checkout.</li>
        <li>Refused deliveries.</li>
        <li>Packages reported as delivered by the carrier but later reported missing or stolen.</li>
        <li>Requests based on misunderstandings of product specifications or intended use.</li>
        <li>Products that have been opened, altered, improperly stored, or otherwise handled after delivery.</li>
      </ul>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">6. Reporting an Order Issue</h3>
      <p>If you experience an issue with your order, please contact us as soon as reasonably possible at info@trueimpactpeptides.com. Please include:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Full Name</li>
        <li>Order Number</li>
        <li>Description of the issue</li>
        <li>Photographs (if applicable)</li>
        <li>Any additional information that may assist our investigation</li>
      </ul>
      <p>Providing complete information allows us to review your request more efficiently.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">7. Investigation Process</h3>
      <p>Each reported issue is reviewed individually. Our review may include:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Order verification</li>
        <li>Shipping records</li>
        <li>Tracking information</li>
        <li>Batch verification</li>
        <li>Packaging review</li>
        <li>Photographs</li>
        <li>Carrier documentation</li>
        <li>Internal fulfillment records</li>
      </ul>
      <p>Additional information may be requested before a final determination is made.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">8. Refunds</h3>
      <p>If True Impact Peptides determines that a refund is appropriate, any approved refund will generally be issued to the original payment method used for the purchase. Refund processing times vary depending on your financial institution and payment processor. Approval of one refund does not establish an obligation to approve future refund requests.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">9. Replacements</h3>
      <p>When appropriate, True Impact Peptides may determine that replacing a product is the most appropriate resolution. Replacement products are shipped only after our investigation has been completed. Replacement decisions remain solely within the discretion of True Impact Peptides.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">10. Chargebacks</h3>
      <p>We encourage customers to contact us before initiating a chargeback through their financial institution. Many order issues can be resolved quickly through our customer support team. Fraudulent or abusive chargebacks may result in:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Order cancellation</li>
        <li>Account suspension</li>
        <li>Permanent account termination</li>
        <li>Collection efforts where permitted by law</li>
        <li>Reporting to applicable payment processors</li>
      </ul>
      <p>Nothing in this policy limits any rights available under applicable law.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">11. Shipping Costs</h3>
      <p>Original shipping charges are generally non-refundable. If True Impact Peptides determines that an order issue resulted from our own fulfillment error, we may cover the cost of shipping any approved replacement product.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">12. Carrier Delays</h3>
      <p>Delivery estimates are provided by shipping carriers and are not guaranteed. True Impact Peptides is not responsible for delays caused by:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Weather</li>
        <li>Natural disasters</li>
        <li>Carrier disruptions</li>
        <li>Government action</li>
        <li>Labor disputes</li>
        <li>Holidays</li>
        <li>Other circumstances beyond our reasonable control</li>
      </ul>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">13. Lost or Stolen Packages</h3>
      <p>If tracking information indicates that a package has been delivered, customers should first contact the shipping carrier. Although we will make reasonable efforts to assist with carrier investigations, True Impact Peptides cannot guarantee reimbursement or replacement for packages confirmed as delivered by the carrier.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">14. Policy Updates</h3>
      <p>True Impact Peptides reserves the right to modify this Policy at any time. Changes become effective immediately upon publication on the Website. The "Last Updated" date indicates when the most recent revision became effective.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">15. Contact Information</h3>
      <p>Questions regarding this Policy may be directed to:</p>
      <p>True Impact Peptides<br />Email: info@trueimpactpeptides.com</p>

      <p className="pt-4 italic text-slate-400">End of Refunds, Replacements &amp; Order Issues Policy</p>
    </LegalPage>
  );
}
